import { motion } from 'framer-motion';
import { Download, ExternalLink, Mail, Calendar } from 'lucide-react';
import StaticPageLayout from '../../components/StaticPageLayout';

const pressReleases = [
  {
    date: '2024-11-15',
    title: 'SweetPilot Raises $25M Series A to Expand AI Workforce Platform',
    excerpt:
      'Funding led by Sequoia Capital will accelerate product development and global expansion.',
  },
  {
    date: '2024-09-20',
    title: 'SweetPilot Launches Brain AI: Custom Knowledge Base for AI Employees',
    excerpt:
      'New feature allows businesses to train AI employees on proprietary data and documents.',
  },
  {
    date: '2024-07-10',
    title: 'SweetPilot Reaches 50,000 Active Users Milestone',
    excerpt:
      'Platform growth accelerates as businesses embrace AI-powered workforce automation.',
  },
  {
    date: '2024-05-01',
    title: 'SweetPilot Emerges from Stealth with AI Employee Platform',
    excerpt:
      'Startup launches with 10 AI employees for marketing, sales, and customer support.',
  },
];

const coverage = [
  {
    publication: 'TechCrunch',
    title: '"SweetPilot is redefining what\'s possible with AI assistants"',
    url: '#',
    logo: '📰',
  },
  {
    publication: 'Forbes',
    title: '"Top 10 AI Startups to Watch in 2024"',
    url: '#',
    logo: '📊',
  },
  {
    publication: 'The Verge',
    title: '"The future of work might be AI employees that never sleep"',
    url: '#',
    logo: '🔮',
  },
  {
    publication: 'Wired',
    title: '"How SweetPilot is democratizing AI for small businesses"',
    url: '#',
    logo: '💡',
  },
];

const stats = [
  { value: '50,000+', label: 'Active Users' },
  { value: '$25M', label: 'Funding Raised' },
  { value: '15', label: 'AI Employees' },
  { value: '100+', label: 'Countries' },
];

export default function Press() {
  return (
    <StaticPageLayout
      title="Press & Media"
      subtitle="News, resources, and media assets for journalists and analysts."
    >
      {/* Key Stats */}
      <section className="mb-16">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-6 rounded-2xl bg-dark-800/50 border border-white/5"
            >
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Press Releases */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Press Releases</h2>
        <div className="space-y-4">
          {pressReleases.map((release, index) => (
            <motion.div
              key={release.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group flex flex-col md:flex-row md:items-start gap-4 p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-white/20 transition-all"
            >
              <div className="flex items-center gap-2 text-gray-500 text-sm md:w-32 shrink-0">
                <Calendar className="w-4 h-4" />
                {new Date(release.date).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white group-hover:text-purple-300 transition-colors mb-2">
                  {release.title}
                </h3>
                <p className="text-gray-400 text-sm">{release.excerpt}</p>
              </div>
              <button className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm shrink-0">
                Read More <ExternalLink className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Media Coverage */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">In the News</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {coverage.map((item, index) => (
            <motion.a
              key={item.publication}
              href={item.url}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group flex items-start gap-4 p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-purple-500/30 transition-all"
            >
              <div className="w-12 h-12 rounded-xl bg-dark-700 flex items-center justify-center text-2xl shrink-0">
                {item.logo}
              </div>
              <div>
                <span className="text-purple-400 text-sm font-medium">{item.publication}</span>
                <p className="text-white mt-1 group-hover:text-purple-300 transition-colors">
                  {item.title}
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-500 group-hover:text-purple-400 transition-colors shrink-0 ml-auto" />
            </motion.a>
          ))}
        </div>
      </section>

      {/* Brand Assets */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-white mb-8">Brand Assets</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { name: 'Logo Package', format: 'ZIP', size: '2.4 MB' },
            { name: 'Brand Guidelines', format: 'PDF', size: '1.8 MB' },
            { name: 'Product Screenshots', format: 'ZIP', size: '8.2 MB' },
          ].map((asset) => (
            <div
              key={asset.name}
              className="p-6 rounded-2xl bg-dark-800/50 border border-white/5 hover:border-white/20 transition-all"
            >
              <h3 className="text-lg font-semibold text-white mb-2">{asset.name}</h3>
              <p className="text-gray-500 text-sm mb-4">
                {asset.format} • {asset.size}
              </p>
              <button className="flex items-center gap-2 text-purple-400 hover:text-purple-300 text-sm">
                <Download className="w-4 h-4" />
                Download
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* Contact */}
      <section className="text-center p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 border border-white/5">
        <h2 className="text-2xl font-bold text-white mb-4">Media Inquiries</h2>
        <p className="text-gray-400 mb-6">
          For press inquiries, interviews, or additional information, please contact our media
          relations team.
        </p>
        <a
          href="mailto:press@flowai.com"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-xl font-medium text-white"
        >
          <Mail className="w-5 h-5" />
          press@flowai.com
        </a>
      </section>
    </StaticPageLayout>
  );
}
